// Whose face an assigned candidate already is (ADR-0042): a candidate confirmed to
// another identity proposes merging the two, so every surface that lists it
// names that identity.
import type { TFunction } from 'i18next'
import { GitMerge } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { FaceMatchingCandidate } from '@/services/api/face-matching'

const PM = 'cases.investigate.entityResolution.photoMatching'

/** The identity a candidate's face is already confirmed to. */
export interface CandidateOwner {
  personId: string
  /** Already resolved, the unknown-identity fallback included. */
  displayName: string
  /** That identity's page; without one the name is not a link. */
  href?: string
}

/** The owner of a candidate's face, or null for a face nobody owns yet. */
export function candidateOwner(
  candidate: Pick<FaceMatchingCandidate, 'assigned_person_id' | 'assigned_display_name'>,
  t: TFunction,
  identityHref?: (personId: string) => string
): CandidateOwner | null {
  const personId = candidate.assigned_person_id
  if (!personId) return null
  return {
    personId,
    displayName: candidate.assigned_display_name ?? t(`${PM}.unknownIdentity`),
    href: identityHref?.(personId),
  }
}

/**
 * "Belongs to identity: Jana", linking to Jana's page. The link's click stops
 * here, so a clickable row around it does not also open the face.
 */
export function CandidateOwnerLine({ owner }: { owner: CandidateOwner }) {
  const { t } = useTranslation()
  return (
    <span
      data-owner-id={owner.personId}
      className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground max-md:flex-wrap max-md:gap-y-0.5"
    >
      <GitMerge className="size-3.5 shrink-0 text-info" aria-hidden />
      <span className="shrink-0">{t(`${PM}.assigned.belongsTo`)}</span>
      {owner.href ? (
        <Link
          to={owner.href}
          className="truncate font-medium text-primary hover:underline"
          onClick={event => event.stopPropagation()}
        >
          {owner.displayName}
        </Link>
      ) : (
        <span className="truncate font-medium text-foreground">{owner.displayName}</span>
      )}
    </span>
  )
}
